import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { http } from "@/lib/api";

const links = [
  { to: "/admin", label: "Dashboard", testId: "adm-nav-dashboard", end: true },
  { to: "/admin/brands", label: "Brands", testId: "adm-nav-brands" },
  { to: "/admin/deleted-brands", label: "Deleted", testId: "adm-nav-deleted" },
  { to: "/admin/pipeline", label: "Pipeline", testId: "adm-nav-pipeline" },
  { to: "/admin/approvals", label: "Approvals", testId: "adm-nav-approvals" },
  { to: "/admin/inbox", label: "Inbox", testId: "adm-nav-inbox" },
  { to: "/admin/bulk-intake", label: "Bulk add", testId: "adm-nav-bulk", adminOnly: true, badge: "bulkPending" },
  { to: "/admin/ops", label: "Ops", testId: "adm-nav-ops" },
  { to: "/admin/claims", label: "Claims", testId: "adm-nav-claims" },
  { to: "/admin/sector-reports", label: "Sector reports", testId: "adm-nav-sector-reports" },
  { to: "/admin/reports", label: "Reports", testId: "adm-nav-reports" },
  { to: "/admin/partners", label: "Partners", testId: "adm-nav-partners" },
  { to: "/admin/users", label: "Users", testId: "adm-nav-users", adminOnly: true },
];

/**
 * Polls the recent-bulk-batches endpoint every 30s to feed the "Bulk add"
 * nav badge (count of pending runs across all batches submitted in the last
 * 7 days). Admin-only — the endpoint is gated already, and non-admins would
 * hit 403 anyway.
 */
function useBulkPending(user) {
  const [pending, setPending] = useState(0);
  useEffect(() => {
    if (!user || user.role !== "ADMIN") return;
    let alive = true;
    const load = () => {
      http.get("/admin/ops/recent-bulk-batches?days=7")
        .then((r) => {
          if (!alive) return;
          const total = (r.data.batches || []).reduce((acc, b) => acc + (b.pending_runs || 0), 0);
          setPending(total);
        })
        .catch(() => { /* silent — endpoint may be transiently unavailable */ });
    };
    load();
    const int = setInterval(load, 30 * 1000);
    return () => { alive = false; clearInterval(int); };
  }, [user]);
  return pending;
}

/** Compute "3h 12m" style remaining time until an ISO date. Returns null when
 * `iso` is missing so callers can hide the badge. */
function useCountdown(iso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return null;
    const secs = Math.max(0, Math.floor((then - now) / 1000));
    if (secs === 0) return { label: "expired", secs: 0, warn: true };
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const label = h >= 1 ? `${h}h ${m}m` : `${m}m`;
    return { label, secs, warn: secs < 30 * 60 };
  }, [iso, now]);
}

function SessionCountdown({ session }) {
  const cd = useCountdown(session?.access_expires_at);
  if (!cd) return null;
  return (
    <span
      className="text-xs font-mono px-2 py-0.5 border"
      style={{
        borderColor: cd.warn ? "#B97700" : "var(--bp-border)",
        background: cd.warn ? "#FFF8E1" : "transparent",
        color: cd.warn ? "#B97700" : "var(--bp-text-muted)",
      }}
      title="Session auto-renews on the next action while your refresh token (7d) is valid."
      data-testid="adm-session-countdown"
    >
      Session ends in {cd.label}
    </span>
  );
}

export default function AdminLayout({ children }) {
  const { user, session, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const bulkPending = useBulkPending(user);
  const badgeCount = { bulkPending };

  return (
    <div className="min-h-screen" style={{ background: "var(--bp-bg)" }}>
      <header className="border-b" style={{ borderColor: "var(--bp-border)", background: "#fff" }}>
        <div className="bp-container py-3 flex items-center gap-8">
          <Link to="/admin" className="font-serif text-xl" data-testid="adm-brand">BrandPassport <span className="text-xs font-mono uppercase tracking-widest ml-2" style={{ color: "var(--bp-text-muted)" }}>Editor console</span></Link>
          <nav className="flex items-center gap-4 text-sm ml-4">
            {links.filter((l) => !l.adminOnly || user?.role === "ADMIN").map((l) => {
              const active = l.end ? loc.pathname === l.to : loc.pathname.startsWith(l.to);
              const count = l.badge ? badgeCount[l.badge] || 0 : 0;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={l.testId}
                  className={`px-2 py-1 border-b-2 inline-flex items-center gap-1.5 ${active ? "border-black" : "border-transparent"}`}
                  style={{ color: "var(--bp-text)" }}
                >
                  <span>{l.label}</span>
                  {count > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-mono font-bold rounded-full bg-rose-600 text-white leading-none"
                      title={`${count} run(s) still pending in bulk batches from the last 7 days`}
                      data-testid={`${l.testId}-badge`}
                    >
                      {count > 99 ? "99+" : count}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-4 text-sm">
            <SessionCountdown session={session} />
            <Link to="/" className="bp-link" data-testid="adm-view-public">View public site →</Link>
            <span style={{ color: "var(--bp-text-muted)" }} className="text-xs font-mono" data-testid="adm-current-user">{user?.email}</span>
            <button onClick={() => { logout(); nav("/admin/login"); }} className="text-sm underline" data-testid="adm-logout">Log out</button>
          </div>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}
